import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import visitService from '../../services/visitService';
import actionItemService from '../../services/actionItemService';
import { VISIT_TYPES, formatVisitId, CTMS_NOTIFICATIONS_REFRESH_EVENT } from '../../utils/constants';
import Alert from '../Common/Alert';
import { ElectronicSign } from '../Common/ElectronicSign';
import './NotificationPanel.css';

const PI_PROFILE_CODE = 'PI';

function formatDate(d) {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

const NotificationPanel = ({ isOpen, onClose, onNotificationCountChange }) => {
  const { user: authUser } = useContext(AuthContext);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [actioningId, setActioningId] = useState(null);
  const [showESignModal, setShowESignModal] = useState(false);
  const pendingActionRef = useRef(null);
  const eSignRemarksRef = useRef('');

  const profileCode = (authUser?.profileCode || authUser?.vUserTypeCode || authUser?.role || '').toString().trim().toUpperCase();
  const isPI = profileCode === PI_PROFILE_CODE.toUpperCase();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      if (isPI) {
        const [visitPending, followUpPending] = await Promise.all([
          visitService.getPINotifications(),
          visitService.getPIFollowUpNotifications(),
        ]);
        const a1 = Array.isArray(visitPending) ? visitPending : [];
        const a2 = Array.isArray(followUpPending) ? followUpPending : [];
        const merged = [
          ...a1.map((v) => ({ kind: 'pi_visit', data: v })),
          ...a2.map((v) => ({ kind: 'pi_followup', data: v })),
        ];
        setList(merged);
        if (onNotificationCountChange) onNotificationCountChange(merged.length);
      } else {
        const data = await actionItemService.getOverdueNotifications();
        const arr = Array.isArray(data) ? data : [];
        setList(arr.map((n) => ({ kind: 'issue_sla', data: n })));
        if (onNotificationCountChange) onNotificationCountChange(arr.length);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setList([]);
      if (onNotificationCountChange) onNotificationCountChange(0);
    } finally {
      setLoading(false);
    }
  }, [isPI, onNotificationCountChange]);

  useEffect(() => {
    if (isOpen) fetchList();
  }, [isOpen, fetchList]);

  useEffect(() => {
    const onRefresh = () => {
      if (isOpen) fetchList();
    };
    window.addEventListener(CTMS_NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(CTMS_NOTIFICATIONS_REFRESH_EVENT, onRefresh);
  }, [isOpen, fetchList]);

  const runPendingAction = useCallback(async () => {
    const action = pendingActionRef.current;
    if (!action) return;
    const id = action.visit.scheduledVisitID ?? action.visit.scheduledVisitId;
    if (!id) return;
    setActioningId(id);
    setToast(null);
    const remark = (eSignRemarksRef.current || '').trim() || undefined;
    try {
      if (action.flow === 'followup') {
        if (action.type === 'approve') {
          await visitService.followUpConfirm(id, { remark });
          setToast({ type: 'success', message: 'Saved Successfully.' });
        } else {
          const rejectReason = remark ? `${remark}` : 'Rejected follow-up from notification';
          await visitService.followUpReject(id, rejectReason);
          setToast({ type: 'success', message: 'Saved Successfully.' });
        }
      } else if (action.type === 'approve') {
        await visitService.confirm(id, { remark });
        setToast({ type: 'success', message: 'Saved Successfully.' });
      } else {
        const rejectReason = remark ? `${remark}` : 'Rejected from notification';
        await visitService.cancel(id, rejectReason);
        setToast({ type: 'success', message: 'Saved Successfully.' });
      }
      await fetchList();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || (action.type === 'approve' ? 'Failed to approve visit.' : 'Failed to reject visit.');
      const isAlreadyRejected = msg === 'Already Rejected';
      setToast({ type: isAlreadyRejected ? 'info' : 'error', message: isAlreadyRejected ? 'Already Rejected' : msg });
    } finally {
      setActioningId(null);
      pendingActionRef.current = null;
      setShowESignModal(false);
    }
  }, [fetchList]);

  const handleApprove = (visit) => {
    const id = visit.scheduledVisitID ?? visit.scheduledVisitId;
    if (!id) return;
    eSignRemarksRef.current = '';
    pendingActionRef.current = { type: 'approve', visit, flow: 'visit' };
    setShowESignModal(true);
  };

  const handleReject = (visit) => {
    const id = visit.scheduledVisitID ?? visit.scheduledVisitId;
    if (!id) return;
    eSignRemarksRef.current = '';
    pendingActionRef.current = { type: 'reject', visit, flow: 'visit' };
    setShowESignModal(true);
  };

  const handleFollowUpApprove = (visit) => {
    const id = visit.scheduledVisitID ?? visit.scheduledVisitId;
    if (!id) return;
    eSignRemarksRef.current = '';
    pendingActionRef.current = { type: 'approve', visit, flow: 'followup' };
    setShowESignModal(true);
  };

  const handleFollowUpReject = (visit) => {
    const id = visit.scheduledVisitID ?? visit.scheduledVisitId;
    if (!id) return;
    eSignRemarksRef.current = '';
    pendingActionRef.current = { type: 'reject', visit, flow: 'followup' };
    setShowESignModal(true);
  };

  if (!isOpen) return null;

  return (
    <div className="notification-overlay notification-overlay-right" aria-hidden="false">
      <div
        className="notification-panel-right"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Notifications"
      >
        <div className="notification-header">
          <h4>Notifications</h4>
          <button type="button" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {toast && (
          <div className="notification-toast">
            <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
          </div>
        )}

        {loading ? (
          <div className="notification-loading">Loading...</div>
        ) : list.length === 0 ? (
          <div className="notification-empty">Up to date</div>
        ) : (
          <div className="notification-scroll">
            <ul className="notification-list">
            {list.map((entry) => {
              if (entry.kind === 'issue_sla') {
                const n = entry.data;
                const id = n.actionItemID ?? n.actionItemId;
                const issueNo = n.actionItemNumber || id || '—';
                const priorityLabel = n.priorityDescription || '—';
                const issueMessage = n.message;
                  
                return (
                  <li key={`issue-${id}`} className="notification-item">
                    <div className="notification-item-row">
                      <strong>Issue Overdue</strong>
                    </div>
                    <div className="notification-item-row">
                      <strong>Issue No:</strong>{' '}
                      <span>{issueNo}</span>
                    </div>
                    <div className="notification-item-row">
                      <strong>Message:</strong>{' '}
                      <span>{issueMessage}</span>
                    </div>
                    <div className="notification-item-row">
                      <strong>Priority:</strong>{' '}
                      <span>{priorityLabel}</span>
                    </div>
                    <div className="notification-item-row">
                      <strong>Project:</strong>{' '}
                      <span>{n.projectCode || '—'}</span>
                    </div>
                    <div className="notification-item-row">
                      <strong>Site:</strong>{' '}
                      <span>{n.siteCode || n.siteDescription || '—'}</span>
                    </div>
                    <div className="notification-item-row">
                      <strong>Visit:</strong>{' '}
                      <span>
                        {(() => {
                          const dc = (n.displayVisitCode ?? n.DisplayVisitCode ?? '').toString().trim();
                          if (dc) return dc;
                          const vt = (n.visitType ?? '').toString().trim();
                          const id2 = n.scheduledVisitID ?? n.scheduledVisitId ?? '';
                          if (vt && id2 !== '' && id2 != null) return `${vt}${id2}`;
                          return '—';
                        })()}
                      </span>
                    </div>
                    <div className="notification-item-row">
                      <strong>Overdue (hours):</strong>{' '}
                      <span>
                        {(() => {
                          const h = Number(n.overdueByHours);
                          return n.overdueByHours != null && n.overdueByHours !== '' && Number.isFinite(h)
                            ? Math.max(0, Math.ceil(h))
                            : '—';
                        })()}
                      </span>
                    </div>
                  </li>
                );
              }
              const visit = entry.data;
              const id = visit.scheduledVisitID ?? visit.scheduledVisitId;
              const visitTypeLabel = VISIT_TYPES[visit.visitType] || visit.visitType || 'Visit';
              const visitIdLabel = formatVisitId ? formatVisitId(visit) : (visit.visitType || '') + (id || '');
              const busy = actioningId === id;
              const isFollowUp = entry.kind === 'pi_followup';

              return (
                <li key={id} className="notification-item">
                  <div className="notification-item-row">
                    <strong>{isFollowUp ? 'Follow-Up Letter:' : 'Visit Type:'}</strong>
                    <span>{visitTypeLabel} ({visitIdLabel})</span>
                  </div>
                  <div className="notification-item-row">
                    <strong>Project:</strong>
                    <span>{visit.projectCode || visit.studyCode || '—'}</span>
                  </div>
                  <div className="notification-item-row">
                    <strong>Site:</strong>
                    <span>{visit.siteCode || visit.siteName || '—'}</span>
                  </div>
                  <div className="notification-item-row">
                    <strong>Scheduled Start Date:</strong>
                    <span>{formatDate(visit.plannedStartDate)}</span>
                  </div>
                  <div className="notification-item-row">
                    <strong>Scheduled End Date:</strong>
                    <span>{formatDate(visit.plannedEndDate)}</span>
                  </div>
                  <div className="notification-item-actions">
                    <button
                      type="button"
                      className="notification-btn-approve"
                      onClick={() => (isFollowUp ? handleFollowUpApprove(visit) : handleApprove(visit))}
                      disabled={busy}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="notification-btn-reject"
                      onClick={() => (isFollowUp ? handleFollowUpReject(visit) : handleReject(visit))}
                      disabled={busy}
                    >
                      Reject
                    </button>
                  </div>
                </li>
              );
            })}
            </ul>
          </div>
        )}
      </div>

      {showESignModal && (
        <ElectronicSign
          show={true}
          onClose={() => {
            setShowESignModal(false);
            pendingActionRef.current = null;
            eSignRemarksRef.current = '';
          }}
          onAuthSuccess={(vRemarks) => { eSignRemarksRef.current = vRemarks || ''; }}
          onSave={runPendingAction}
        />
      )}
    </div>
  );
};

export default NotificationPanel;
